// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { PanelRowPresentationProps } from "@steiner/MobiusOverrides/PanelMenu/PanelRow";
import { navigationDrawerStyles } from "@steiner/Overrides/Components/NavigationDrawer";
import NavArrowRight from "@steiner/XDL/Icons/NavArrowRight";
import { buttonTransparent } from "@steiner/XDL/PreStyle/button";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { color, ellipsis, getSystemColor, styledScrollbar, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

const sharedLinkWrapperStyle: PanelRowPresentationProps = {
    moreIconProps: {
        color: "common.ui5",
        src: NavArrowRight,
        size: 12,
        css: css`
            width: 30px;
            height: 30px;
            justify-content: center;
            &&& {
                background: transparent;
            }
        `,
    },
    css: css`
        width: 100%;
        padding: 6px 0 6px 16px;
        background: transparent;
        box-shadow: 0 1px 0 0 ${color("common.border")};
        gap: 16px;
    `,
};

const sharedLinkTextStyle: TypographyPresentationProps = {
    ellipsis: true,
    size: 14,
    lineHeight: "22px",
    transform: "capitalize",
    color: "text",
    css: css`
        white-space: initial;
        ${ellipsis(1)}
    `,
};

const sharedCloseWrapper: InjectableCss = {
    css: css`
        display: flex;
        background-color: ${color("common.ui2")};
        box-shadow: 0 1px 0 0 ${color("common.ui6")};
        padding: 8px 16px;
    `,
};

const sharedCloseLink: LinkPresentationProps = {
    icon: {
        iconProps: {
            src: "X",
            color: "common.ui7",
            size: 12,
            css: css`
                opacity: 0.65;
                svg {
                    stroke-width: 4px;
                }
            `,
        },
    },
    css: css`
        ${TypographyStyle} {
            color: ${color("common.ui7")};
        }
    `,
};

navigationDrawerStyles.menuTriggerButton = {
    css: css`
        ${buttonTransparent}

        display: flex;
        align-items: flex-end;
        gap: 8px;
        padding: 0;

        & > ${TypographyStyle} {
            font-size: 14px;
            font-weight: bold;
            line-height: 18px;
            text-align: left;
        }
        & > ${IconWrapper} {
            top: 0;
        }
    `,
};

navigationDrawerStyles.drawer = {
    size: 344,
    mergeCss: true,
};

navigationDrawerStyles.panelMenu = {
    cssOverrides: {
        wrapper: css`
            text-align: left;
            background: transparent;
            box-shadow: 0 1px 0 0 ${color("common.border")};

            > a,
            button {
                height: auto;
                box-shadow: none;
            }
        `,
        menu: css`
            height: 100%;
            padding-bottom: 1px;
            ${styledScrollbar()};
        `,
    },
    panelRowStyle: {
        css: css`
            display: none;
        `,
    },
    closeTitleWrapper: {
        ...sharedCloseWrapper,
    },
    closeTitle: {
        ...sharedCloseLink,
    },
    childTypographyProps: {
        ...sharedLinkTextStyle,
    },
    panelRowProps: {
        ...sharedLinkWrapperStyle,
    },
    parentLinkWrapper: {
        css: css`
            display: flex;
            background-color: ${color("common.ui17")};
            box-shadow: 0 1px 0 0 ${color("common.ui6")};
            padding: 8px 16px;
            margin-top: 2px;
        `,
    },
    parentLinkText: {
        icon: {
            iconProps: {
                size: 16,
                src: "ArrowLeft",
                css: css`
                    svg {
                        stroke-width: 3px;                    
                    }
                `,
            },
        },
        typographyProps: {
            ellipsis: true,
        },
        css: css`
            width: 100%;
        `,
    },
};

navigationDrawerStyles.closeTitleWrapper = {
    ...sharedCloseWrapper,
};

navigationDrawerStyles.closeTitle = {
    ...sharedCloseLink,
};

navigationDrawerStyles.drawerSectionWrapper = {};

navigationDrawerStyles.mainNavigationRow = {
    ...sharedLinkWrapperStyle,
};

navigationDrawerStyles.mainNavigationRowTypography = {
    ...sharedLinkTextStyle,
    color: "text",
    css: css`
        width: 100%;
        text-align: left;
        ${({ isFontThick }: any) => {
            if (isFontThick) {
                return css`
                    font-weight: ${fontConfig.weight.semiBold};
                `;
            }
        }}
    `,
};

navigationDrawerStyles.logoLinks = {
    typographyProps: {
        ...sharedLinkTextStyle,
    },
};

navigationDrawerStyles.signOutRow = {
    ...sharedLinkWrapperStyle,
};

navigationDrawerStyles.signOutRowText = {
    ...sharedLinkTextStyle,
};
